/* Example URL: https://nxt3.staging.lizard.net/api/v4/timeseries/a0f2bfe3-3cbd-4084-adbe-5cca5d2feb2f/aggregates/?start=2021-01-25T00:00:00Z&end=2022-02-04T00:00:00Z&min_points=200&fields=min,max */

import { z } from "zod";

import { fetchWithError } from "@nens/react-query-utils";
import { combineUrlAndParams } from "@nens/shared-utils";

import { lizardDateSchema } from "../dates";
import { TimeseriesEvent } from "../types";

/* Note fields we ask are hardcoded right now. */

const aggregatesSchema = z.object({
  results: z.array(
    z.object({
      time: lizardDateSchema.nullable(),
      sum: z.number().nullable().optional(),
      avg: z.number().nullable().optional(),
    })
  ),
});

export async function fetchTimeseriesAggregates(
  uuid: string,
  aggregation: "avg" | "sum",
  start: Date,
  end: Date,
  min_points: number,
  params: Record<string, string | number> = {}
) {
  const url = combineUrlAndParams(`/api/v4/timeseries/${uuid}/aggregates/`, {
    start: start.toISOString(),
    end: end.toISOString(),
    min_points,
    fields: "time," + aggregation,
    closed: "right",
    label: "left",
    ...params,
  });

  const json = await fetchWithError(url);
  const results = aggregatesSchema.parse(json).results;

  return results
    .filter((event) => event.time !== null)
    .map((event) => {
      return {
        time: event.time as Date,
        value: aggregation === "avg" ? event.avg : event.sum,
      } as TimeseriesEvent;
    });
}

export async function fetchTimeseriesAggregatesByWindow(
  uuid: string,
  aggregation: "avg" | "sum",
  start: Date,
  end: Date,
  window: string,
  params: Record<string, string | number> = {}
) {
  const url = combineUrlAndParams(`/api/v4/timeseries/${uuid}/aggregates/`, {
    start: start.toISOString(),
    end: end.toISOString(),
    window,
    fields: "time," + aggregation,
    closed: "right",
    label: "left",
    ...params,
  });

  const json = await fetchWithError(url);
  const results = aggregatesSchema.parse(json).results;

  return results
    .filter((event) => event.time !== null)
    .map((event) => {
      return {
        time: event.time as Date,
        value: aggregation === "avg" ? event.avg : event.sum,
      } as TimeseriesEvent;
    });
}

const MINUTE = 60 * 1000;
const FIVE_MINUTES = 5 * 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;
const MONTH = 31 * DAY;
const YEAR = 365 * DAY;

export function convertMsToClosestFrequency(ms: number) {
  // Different frequency formats for timeseries and raster
  if (ms <= FIVE_MINUTES) {
    return "5min";
  } else if (ms <= HOUR) {
    return "h";
  } else if (ms <= DAY) {
    return "d";
  } else if (ms <= WEEK) {
    return "w";
  } else {
    return "MS";
  }
}

export function getTimeResolution(periodMs: number) {
  if (periodMs <= DAY) {
    return FIVE_MINUTES;
  } else if (periodMs <= WEEK) {
    return HOUR;
  } else if (periodMs <= MONTH) {
    return DAY;
  } else if (periodMs <= YEAR / 2) {
    return WEEK;
  } else {
    return MONTH;
  }
}

export function getFrequency(periodMs: number, intervalMs: number) {
  const frequency = getTimeResolution(periodMs);
  return convertMsToClosestFrequency(frequency >= intervalMs ? frequency : intervalMs);
}
